import { cloneValue, getAtPath, setAtPath } from './configPaths'
import { applyPreset } from './schemaV2'
import { isSliderV3 } from './sliderValidation'

/**
 * Switching between templates of the Slider content engine (Split Slider 3, Story, Reel) keeps
 * the ad: the same slots, colour roles, skins and brand fields mean the same thing in each
 * (AD-204: "a config that renders as a Slider renders as a Story by setting the template").
 * Only choices the target template does not have are left at its defaults.
 */

const HEX = /^#[0-9a-f]{6}$/i
const NUMERIC = new Set(['range', 'integer', 'number', 'duration'])
const TEXTUAL = new Set(['text', 'textarea', 'url', 'email', 'tel', 'rich-text', 'date', 'time', 'datetime'])

export function carriesContent(from, toDefaults) {
  return isSliderV3(from) && isSliderV3(toDefaults)
}

/** Whether `value` is a valid value of `field` in the target template. */
export function acceptsValue(field, value) {
  if (value === undefined) return false
  if (Array.isArray(field.options) && field.options.length) {
    return field.options.some((option) => option.value === value)
  }
  const rules = field.validation || {}
  if (NUMERIC.has(field.type)) {
    const number = Number(value)
    if (value === null || value === '' || !Number.isFinite(number)) return false
    return !(rules.min !== undefined && number < rules.min) && !(rules.max !== undefined && number > rules.max)
  }
  if (field.type === 'boolean') return typeof value === 'boolean'
  if (field.type === 'color') return typeof value === 'string' && HEX.test(value)
  if (TEXTUAL.has(field.type)) {
    if (value === null) return true
    return typeof value === 'string' && !(rules.maxLength && value.length > rules.maxLength)
  }
  if (field.type === 'string-list') {
    return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
      && !(rules.maxItems && value.length > rules.maxItems)
  }
  return false
}

// The picture a slide shows: its image, or a Reel's poster when coming from a Reel.
function slideImage(slide) {
  if (slide?.media?.sourceAssetId) return slide.media
  if (slide?.video?.poster?.sourceAssetId) return slide.video.poster
  return null
}

/**
 * `next` is the target template's defaults with the format and campaign already settled.
 * Returns the carried configuration and the image paths that still need a crop (crop-mode
 * profiles need one before the ad validates; the caller renders it).
 */
export function carryTemplateContent(from, next, template) {
  const { schema, manifest } = template
  let result = cloneValue(next)
  const needsCrop = []
  const profiles = manifest?.assetProfiles || {}

  // A named style pack maps onto the target's own tuning of that pack (preset ids are
  // `<format>-<pack>`; the target may offer other formats); custom styles carry axis by axis.
  const fromFormat = from?.format?.id || ''
  const pack = typeof from?.presetId === 'string' && from.presetId.startsWith(`${fromFormat}-`)
    ? from.presetId.slice(fromFormat.length + 1)
    : null
  const presetId = pack ? `${result.format?.id}-${pack}` : null
  const preset = presetId && template.presets?.[presetId]
  if (preset) result = applyPreset(result, preset)

  for (const section of schema.sections || []) {
    for (const field of section.fields || []) {
      if (preset && field.path.startsWith('data.style.')) continue
      const value = getAtPath(from, field.path)
      if (field.type === 'image-editor') {
        if (value && typeof value === 'object' && value.sourceAssetId) result = setAtPath(result, field.path, cloneValue(value))
        continue
      }
      if (acceptsValue(field, value)) result = setAtPath(result, field.path, cloneValue(value))
    }
  }

  for (const collection of schema.collections || []) {
    const sources = getAtPath(from, collection.path, []) || []
    const identity = collection.identityPath || 'id'
    const count = Math.max(collection.minItems || 1, Math.min(sources.length, collection.maxItems || sources.length))
    const fields = (collection.itemSections || []).flatMap((itemSection) => itemSection.fields || [])
    const items = []
    for (let index = 0; index < count; index += 1) {
      const source = sources[index] || {}
      let item = cloneValue(collection.itemDefaults || {})
      item = setAtPath(item, identity, getAtPath(source, identity) || `slide-${index + 1}`)
      for (const field of fields) {
        if (field.type === 'video-editor') continue
        if (field.type === 'image-editor') {
          const media = slideImage(source)
          if (!media) continue
          const focalPoint = media.focalPoint || null
          if (profiles[field.profile]?.mode === 'crop') {
            item = setAtPath(item, field.path, {
              sourceAssetId: media.sourceAssetId, renditionAssetId: null, crop: null,
              focalPoint: focalPoint || { x: 50, y: 50 },
            })
            needsCrop.push(`${collection.path}.${index}.${field.path}`)
          } else {
            item = setAtPath(item, field.path, {
              sourceAssetId: media.sourceAssetId, renditionAssetId: media.sourceAssetId, crop: null,
              ...(focalPoint ? { focalPoint } : {}),
            })
          }
          continue
        }
        const value = getAtPath(source, field.path)
        if (acceptsValue(field, value)) item = setAtPath(item, field.path, cloneValue(value))
      }
      items.push(item)
    }
    result = setAtPath(result, collection.path, items)
  }

  result.presetId = preset ? presetId : null
  if (from?.suggestions) result.suggestions = cloneValue(from.suggestions)
  // Saved sources stay reusable: the server copies them into the new revision by id.
  result.assets = { ...(from?.assets || {}), ...(result.assets || {}) }
  return { configuration: result, needsCrop }
}
